import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FileDragAndDropComponent } from '@lockton-apps/shared/ui/lui';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import {
  castArray,
  compact,
  filter,
  flatMap,
  join,
  map,
  omit,
  reject,
} from 'lodash';

interface FileInputProps extends FormlyFieldProps {
  accept?: string[];
  filesizeLimit?: number;
  filesizeLimitDescription?: string;
  multiple: boolean;
  tooltip: string;
  hint?: string;
  fileUploadHandler?: (file: File[]) => Promise<FileInputFile[]>;
  fileDownloadHandler?: (fileId: string, name?: string) => Promise<Blob>;
  fileDeleteHandler?: (fileId: string, name: string) => Promise<Blob>;
}

interface FileInputFile {
  name: string;
  fileId: string;
  status?: 'idle' | 'uploading' | 'uploaded' | 'error';
}

interface LegacyFileInputFile {
  name: string;
  src: string;
  isImage: boolean;
}

interface LocalFile {
  name: string;
  previewUrl: string;
}

@Component({
    selector: 'lockton-apps-file-input-type',
    templateUrl: './file-input-type.component.html',
    styleUrls: ['./file-input-type.component.scss'],
    standalone: false
})
export class FileInputTypeComponent
  extends FieldType<FieldTypeConfig<FileInputProps>>
  implements OnInit
{
  @ViewChild(FileDragAndDropComponent, { static: true })
  dragAndDropComponent!: FileDragAndDropComponent;

  rejectedFileNames: string[] = [];
  acceptedFormats: string[] = [];
  acceptedFormatNames!: string;

  pendingFiles: LocalFile[] = [];

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    this.acceptedFormats = compact(flatMap(this.props.accept, 'extensions'));
    this.acceptedFormatNames = join(map(this.props.accept, 'name'), ', ');
  }

  async onFilesAdded(files: File | File[]): Promise<void> {
    const filesArr = compact(castArray(files));
    let _files: FileInputFile[] = [];
    let rejectedFiles: FileInputFile[] = [];

    this.rejectedFileNames = map(rejectedFiles, 'name');
    this.pendingFiles = await this.localHandler(filesArr);
    this.cd.markForCheck();

    if (!this.props.fileUploadHandler) {
      console.error('No upload handler provided');
      return;
    }
    try {
      const allFiles = await this.props.fileUploadHandler(filesArr);
      _files = filter(allFiles, (file) => file.status !== 'error');
      rejectedFiles = filter(allFiles, (file) => file.status === 'error');
    } catch (error) {
      console.error(error);
    }
    const currentValue = this.formControl.value || [];
    const newFiles = map(_files, (file) => omit(file, 'status'));
    this.setValue([...currentValue, ...newFiles]);
    this.pendingFiles = [];
  }

  onFilesRejected(files: File | File[]): void {
    this.rejectedFileNames = map(castArray(files), 'name');
  }

  async removeFile(inputFile: FileInputFile | LegacyFileInputFile) {
    if (this.isLegacyFile(inputFile)) {
      return this.setValue(
        reject(this.formControl.value, { src: inputFile.src })
      );
    }

    if (this.isFile(inputFile)) {
      if (!this.props.fileDeleteHandler) {
        console.error('No delete handler provided');
        return;
      }

      try {
        await this.props.fileDeleteHandler(inputFile.fileId, inputFile.name);

        this.setValue(
          reject(this.formControl.value, { fileId: inputFile.fileId })
        );
      } catch (error) {
        console.error(error);
      }
    }
  }

  async downloadFile(file: FileInputFile) {
    await this.props.fileDownloadHandler?.(file.fileId, file.name);
  }

  isFile(file: FileInputFile | LegacyFileInputFile): file is FileInputFile {
    return (file as FileInputFile).fileId !== undefined;
  }

  isLegacyFile(
    file: FileInputFile | LegacyFileInputFile
  ): file is LegacyFileInputFile {
    return (file as LegacyFileInputFile).src !== undefined;
  }

  private setValue(files: FileInputFile[]): void {
    this.formControl.setValue(files);
    this.formControl.markAsDirty();
    this.cd.markForCheck();
  }

  private async localHandler(files: File[]): Promise<LocalFile[]> {
    return await Promise.all(
      map(files, (file: File) => {
        return new Promise(
          (resolve: (file: LocalFile) => void, reject: () => void) => {
            const reader: FileReader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
              return resolve({
                name: file.name,
                previewUrl: event.target?.result as string,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }
        );
      })
    );
  }
}
